const TrackingModule = (() => {

  // ------------------------------------------------------------------
  // CONSTANTS
  // ------------------------------------------------------------------

  const KEYS = {
    FIRST_CLICK:      'qs_first_click',
    FIRST_VISIT:      'qs_first_visit',
    URL_EVENT_SOURCE: 'qs_url_event_source',
    FIRST_PAGE_URL:   'qs_first_page_url',
    FIRST_REFERRER:   'qs_first_referrer',
    VISIT_COUNT:      'qs_visit_count',
    LAST_VISIT:       'qs_last_visit',
    SESSION_COUNTED:  'qs_session_counted', // sessionStorage only
  };

  const AD_PARAMS = [
    'gclid', 'gbraid', 'wbraid',
    'fbclid', 'msclkid', 'ttclid',
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term',
    'ref', 'source',
  ];

  // ------------------------------------------------------------------
  // GATE — who gets first_visit / first_click
  //   false → every visitor
  //   true  → only visitors who arrived with an ad/UTM param (AD_PARAMS)
  // Can also be set per page without editing this file:
  //   <script>window.TRACKING_ONLY_AD_TRAFFIC = true;</script>  (before this script)
  //   or TrackingModule.init({ onlyAdTraffic: true })
  // ------------------------------------------------------------------
  const ONLY_AD_TRAFFIC = false;

  let initialized = false;
  let onlyAdTraffic = ONLY_AD_TRAFFIC;

  // ------------------------------------------------------------------
  // HELPERS
  // ------------------------------------------------------------------

  function parseParams(urlString) {
    try {
      const url = new URL(urlString);
      const params = {};
      url.searchParams.forEach((value, key) => { params[key] = value; });
      return Object.keys(params).length > 0 ? params : null;
    } catch { return null; }
  }

  function hasAdSignal(params) {
    if (!params) return false;
    return AD_PARAMS.some(key => key in params && params[key] !== '');
  }

  function ssGet(key) {
    try { return sessionStorage.getItem(key); } catch { return null; }
  }

  function lsGet(key) {
    try { return localStorage.getItem(key); } catch { return null; }
  }

  // Reads session first, then the localStorage mirror — so "first_*" values
  // survive across sessions instead of being overwritten on the next visit.
  function get(key) {
    return ssGet(key) || lsGet(key);
  }

  function ssSet(key, value) {
    try {
      sessionStorage.setItem(key, value);
      localStorage.setItem(key, value);    // mirror to localStorage as well
    } catch { /* silent */ }
  }

  function toIso(raw) {
    if (!raw) return null;
    const d = new Date(Number(raw));
    return isNaN(d.getTime()) ? null : d.toISOString();
  }

  function safeParse(json) {
    try { return JSON.parse(json); } catch { return null; }
  }

  // ------------------------------------------------------------------
  // CAPTURE ON PAGE LOAD
  // ------------------------------------------------------------------

  function captureEntrySignals() {
    const params = parseParams(window.location.href);
    const now = String(Date.now());

    // ① url_event_source — always save raw params if present (first time only)
    if (params && !get(KEYS.URL_EVENT_SOURCE)) {
      ssSet(KEYS.URL_EVENT_SOURCE, JSON.stringify(params));
    }

    // ② first_page_url + first_referrer — first page this browser ever landed on
    if (!get(KEYS.FIRST_PAGE_URL)) {
      ssSet(KEYS.FIRST_PAGE_URL, window.location.href);
      ssSet(KEYS.FIRST_REFERRER, document.referrer || '');
    }

    // ③ visit_count — +1 once per browser session (not per page load / reload)
    if (!ssGet(KEYS.SESSION_COUNTED)) {
      const count = parseInt(lsGet(KEYS.VISIT_COUNT) || '0', 10) + 1;
      ssSet(KEYS.VISIT_COUNT, String(count));
      try { sessionStorage.setItem(KEYS.SESSION_COUNTED, '1'); } catch { /* silent */ }
    }

    // ④ last_visit — updated every page load
    ssSet(KEYS.LAST_VISIT, now);

    // ⑤ first_visit — first page load for this browser. Never overwritten.
    //    Gate on: only recorded on a visit that arrives with an ad/UTM param.
    const isAdVisit = hasAdSignal(params);
    if (!get(KEYS.FIRST_VISIT) && (!onlyAdTraffic || isAdVisit)) {
      ssSet(KEYS.FIRST_VISIT, now);
    }

    // ⑥ first_click — the first click/tap anywhere on the page, ever.
    //    Gate on: only once this visitor has a recorded ad visit (so an ad
    //    visitor who clicks on a later page is still captured).
    //    Capture phase so it still fires when a button/link/modal calls
    //    stopPropagation(); pointerdown covers taps that never become a click.
    const qualifies = !onlyAdTraffic || !!get(KEYS.FIRST_VISIT);
    if (qualifies && !get(KEYS.FIRST_CLICK)) {
      const events = ['click', 'pointerdown', 'touchstart'];
      const recordFirstClick = () => {
        if (!get(KEYS.FIRST_CLICK)) ssSet(KEYS.FIRST_CLICK, String(Date.now()));
        events.forEach(evt => document.removeEventListener(evt, recordFirstClick, true));
      };
      events.forEach(evt => document.addEventListener(evt, recordFirstClick, { capture: true, passive: true }));
    }
  }

  // ------------------------------------------------------------------
  // BUILD PAYLOAD
  // ------------------------------------------------------------------

  function getPayload() {
    const firstClick   = toIso(get(KEYS.FIRST_CLICK));
    const firstVisit   = toIso(get(KEYS.FIRST_VISIT));
    const lastVisit    = toIso(get(KEYS.LAST_VISIT));
    const urlSource    = get(KEYS.URL_EVENT_SOURCE) || null;
    const leadPageUrl  = window.location.href;
    const firstPageUrl = get(KEYS.FIRST_PAGE_URL) || leadPageUrl;
    const firstRef     = get(KEYS.FIRST_REFERRER) || null;
    const visitCount   = parseInt(get(KEYS.VISIT_COUNT) || '1', 10);

    const extra = {
      page_url:       firstPageUrl,
      first_page_url: firstPageUrl,
      first_referrer: firstRef,
      visit_count:    visitCount,
      last_visit:     lastVisit,
    };

    // tracking_metadata — use rich data from enhanced script if loaded
    // (with our extra fields merged in), otherwise the simple bundle.
    let trackingMetadata;
    if (typeof window.getLeadTrackingData === 'function') {
      try {
        const enhanced = Object.assign({}, window.getLeadTrackingData());
        // Don't let an empty first_click / first_visit from the enhanced
        // script hide the values this module recorded.
        if (enhanced.first_click == null || enhanced.first_click === '') enhanced.first_click = firstClick;
        if (enhanced.first_visit == null || enhanced.first_visit === '') enhanced.first_visit = firstVisit;
        trackingMetadata = JSON.stringify(Object.assign(enhanced, extra));
      } catch {
        trackingMetadata = buildSimpleMetadata(firstClick, firstVisit, urlSource, leadPageUrl, extra);
      }
    } else {
      trackingMetadata = buildSimpleMetadata(firstClick, firstVisit, urlSource, leadPageUrl, extra);
    }

    return {
      first_click:       firstClick,
      first_visit:       firstVisit,
      url_event_source:  urlSource,
      lead_page_url:     leadPageUrl,
      page_url:          firstPageUrl,   // forms read t.page_url in buildTracking()
      first_page_url:    firstPageUrl,
      first_referrer:    firstRef,
      visit_count:       visitCount,
      last_visit:        lastVisit,
      tracking_metadata: trackingMetadata,
    };
  }

  function buildSimpleMetadata(firstClick, firstVisit, urlSource, leadPageUrl, extra) {
    return JSON.stringify(Object.assign({
      first_click:      firstClick,
      first_visit:      firstVisit,
      url_event_source: urlSource ? safeParse(urlSource) : null,
      lead_page_url:    leadPageUrl,
    }, extra));
  }

  // ------------------------------------------------------------------
  // INIT
  // ------------------------------------------------------------------

  // Safe to call from more than one script on the same page — runs once.
  // Optional: init({ onlyAdTraffic: true }) to switch the gate on for this page.
  function init(options) {
    if (initialized) return;
    initialized = true;

    if (options && typeof options.onlyAdTraffic === 'boolean') {
      onlyAdTraffic = options.onlyAdTraffic;
    } else if (typeof window.TRACKING_ONLY_AD_TRAFFIC === 'boolean') {
      onlyAdTraffic = window.TRACKING_ONLY_AD_TRAFFIC;
    }

    captureEntrySignals();
  }

  return { init, getPayload };

})();